import { gql, useQuery } from "@apollo/client";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import type { FC } from "react";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import {
  AiFindingsForAccessionNumberDocument,
  type AiFindingsForAccessionNumberQuery,
} from "./__generated__/ai-findings-panel.gql";

gql`
  query AiFindingsForAccessionNumber($accessionNumber: String!) {
    aiFindingsForAccessionNumber(accessionNumber: $accessionNumber) {
      id
      sourceSystem
      resolutionPath
      receivedAt
      payload
    }
  }
`;

type AiFinding =
  AiFindingsForAccessionNumberQuery["aiFindingsForAccessionNumber"][number];

interface FhirObservation {
  id?: string;
  code?: { text?: string; coding?: { display?: string }[] };
  valueString?: string;
  valueQuantity?: { value?: number; unit?: string };
  valueCodeableConcept?: { text?: string; coding?: { display?: string }[] };
}

interface FhirDiagnosticReportPayload {
  conclusion?: string;
  contained?: FhirObservation[];
}

const observationLabel = (obs: FhirObservation): string => {
  const codeLabel =
    obs.code?.text ?? obs.code?.coding?.[0]?.display ?? "Observation";
  if (obs.valueString) {
    return `${codeLabel}: ${obs.valueString}`;
  }
  if (obs.valueQuantity) {
    const { value, unit } = obs.valueQuantity;
    return `${codeLabel}: ${value ?? ""}${unit ? ` ${unit}` : ""}`.trim();
  }
  if (obs.valueCodeableConcept) {
    const v =
      obs.valueCodeableConcept.text ??
      obs.valueCodeableConcept.coding?.[0]?.display ??
      "—";
    return `${codeLabel}: ${v}`;
  }
  return codeLabel;
};

const FindingRow: FC<{ finding: AiFinding }> = ({ finding }) => {
  const payload = (finding.payload ?? {}) as FhirDiagnosticReportPayload;
  const observations =
    payload.contained?.filter((c) => c && observationLabel(c)) ?? [];
  return (
    <Box
      data-testid="ai-finding"
      style={{
        border: `1px solid ${EPOCUS_BORDER}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        backgroundColor: EPOCUS_WHITE,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: 6 }}
      >
        <Typography
          style={{
            fontSize: rem(12),
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          {finding.sourceSystem ?? "AI source"}
        </Typography>
        <Typography style={{ fontSize: rem(11), color: EPOCUS_TEXT_SECONDARY }}>
          {finding.resolutionPath}
        </Typography>
      </Box>
      {payload.conclusion && (
        <Typography
          style={{
            fontSize: rem(14),
            color: EPOCUS_TEXT_PRIMARY,
            marginBottom: 8,
          }}
        >
          {payload.conclusion}
        </Typography>
      )}
      {observations.length > 0 && (
        <Box component="ul" style={{ margin: 0, paddingLeft: 18 }}>
          {observations.map((obs) => (
            <li
              key={obs.id ?? observationLabel(obs)}
              style={{
                fontSize: rem(13),
                color: EPOCUS_TEXT_SECONDARY,
                marginBottom: 2,
              }}
            >
              {observationLabel(obs)}
            </li>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const AiFindingsPanel: FC<{ accessionNumber: string }> = ({
  accessionNumber,
}) => {
  const { data, loading, error } = useQuery(
    AiFindingsForAccessionNumberDocument,
    {
      variables: { accessionNumber },
      fetchPolicy: "cache-and-network",
    },
  );

  const findings = data?.aiFindingsForAccessionNumber ?? [];

  return (
    <Box
      data-testid="ai-findings-panel"
      style={{
        border: `1px solid ${EPOCUS_PRIMARY}`,
        borderRadius: 12,
        padding: 16,
        backgroundColor: EPOCUS_BG,
      }}
    >
      <Typography
        style={{
          fontSize: rem(13),
          fontWeight: 700,
          color: EPOCUS_PRIMARY,
          marginBottom: 10,
          textTransform: "uppercase",
          letterSpacing: 0.6,
        }}
      >
        AI findings
      </Typography>
      {loading && findings.length === 0 && (
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <CircularProgress size={16} />
          <Typography
            style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}
          >
            Loading AI findings…
          </Typography>
        </Box>
      )}
      {error && (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          Couldn’t load AI findings.
        </Typography>
      )}
      {!loading && !error && findings.length === 0 && (
        <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
          No AI findings posted for this study yet.
        </Typography>
      )}
      {findings.map((finding) => (
        <FindingRow key={finding.id} finding={finding} />
      ))}
    </Box>
  );
};
